import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { simProductsQueries } from '../queries/simProductsQueries';
import { useAtomValue } from 'jotai';
import {
  pageFilterAtom,
  rowsPerPageFilterAtom,
  simProductsFiltersAtom,
  simProductsSortingAtomWithStorage,
} from 'atoms/filters/sim-products';
import { Tag } from 'tags/models/tags';
import { CustomFieldProp } from 'admin/customFields/entities/customFields.entity';
import { SimData } from 'src/common/components/Sim/constants/sim';

export const useRefreshSimProdcutInventoryEntry = () => {
  const filters = useAtomValue(simProductsFiltersAtom);
  const { tags, searchText, chosenIdType, customFields, ...otherFilters } = filters;
  const sorting = useAtomValue(simProductsSortingAtomWithStorage);
  const rowsPerPage = useAtomValue(rowsPerPageFilterAtom);
  const page = useAtomValue(pageFilterAtom);

  const queryClient = useQueryClient();

  const queryKey = simProductsQueries.list({
    searchText: searchText,
    chosenIdType,
    page,
    rowsPerPage,
    ...otherFilters,
    tags,
    sorting,
    customFields,
  });

  const refreshTags = useCallback(
    async ({ cardId, tags }: { cardId: string; tags: Tag[] }) => {
      await queryClient.cancelQueries({ queryKey: queryKey });

      queryClient.setQueryData(queryKey, (oldData: any) => {
        const newData = {
          ...oldData,
          items: oldData?.items.map((simProfile: SimData) => {
            if (simProfile.iccid === cardId) {
              return { ...simProfile, tags: { items: tags, hasMore: false } };
            }

            return simProfile;
          }),
        };
        return newData;
      });
    },
    [queryClient, queryKey],
  );

  const refreshCustomFields = useCallback(
    async ({
      iccid,
      customFieldsUpdate,
    }: {
      iccid: string;
      customFieldsUpdate: Record<string, CustomFieldProp>;
    }) => {
      await queryClient.cancelQueries({ queryKey: queryKey });

      queryClient.setQueryData(queryKey, (oldData: any) => {
        const newData = {
          ...oldData,
          items: oldData?.items.map((simProfile: SimData) => {
            if (simProfile.iccid === iccid) {
              return {
                ...simProfile,
                customFields: Object.values(customFieldsUpdate),
                ...customFieldsUpdate,
              };
            }

            return simProfile;
          }),
        };
        return newData;
      });
    },
    [queryClient, queryKey],
  );

  return {
    refreshTags,
    refreshCustomFields,
  };
};
